import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { formatDateTime } from '@/utils/locale';
import type { ReleaseRun, VersionInfo } from '@/api/releaseApproval';
import { STATUS_PALETTE, versionStatuses, type ProductionVersionStatus, type TestVersionStatus } from './versionStatus';

interface VersionCardProps {
  readonly title: string;
  readonly version: string;
  readonly installedAt: string | null;
  readonly status: TestVersionStatus | ProductionVersionStatus;
  readonly statusLabel: string;
  readonly testId: string;
}

function VersionCard({ title, version, installedAt, status, statusLabel, testId }: VersionCardProps) {
  const { t } = useTranslation('releaseApproval');
  const palette = STATUS_PALETTE[status];
  return (
    <Card
      data-testid={testId}
      data-status={status}
      sx={{
        flex: 1,
        borderLeft: 4,
        borderColor: palette ? `${palette}.main` : 'divider',
      }}
    >
      <CardContent>
        <Typography variant="overline">{title}</Typography>
        <Typography variant="h6">{version}</Typography>
        {installedAt && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('installedAt', { time: formatDateTime(installedAt) })}
          </Typography>
        )}
        <Chip
          size="small"
          color={palette ?? 'default'}
          variant={palette ? 'filled' : 'outlined'}
          label={statusLabel}
          {...(status === 'releasing' ? { icon: <CircularProgress size={12} color="inherit" /> } : {})}
        />
      </CardContent>
    </Card>
  );
}

/**
 * The test version and production side by side, coloured by what waits (#3481):
 * green when both run the same version, yellow while a release runs, and the
 * test card yellow when it holds a version production does not have yet.
 */
export function VersionCards({
  candidate,
  production,
  latestRun,
}: {
  readonly candidate: VersionInfo;
  readonly production: VersionInfo | null;
  readonly latestRun: ReleaseRun | null;
}) {
  const { t } = useTranslation('releaseApproval');
  const status = versionStatuses({ candidate, production, latestRun });
  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <VersionCard
          testId="version-card-test"
          title={t('candidate')}
          version={candidate.version}
          installedAt={candidate.installedAt}
          status={status.test}
          statusLabel={t(`versionStatus.test.${status.test}`)}
        />
        <VersionCard
          testId="version-card-production"
          title={t('production')}
          version={production?.version ?? t('unknownVersion')}
          installedAt={production?.installedAt ?? null}
          status={status.production}
          statusLabel={t(`versionStatus.production.${status.production}`)}
        />
      </Stack>
    </Box>
  );
}
